import { useMemo, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { RestLicenseChangeEntry } from "@KassaroAB/shared/types/licenseChanges";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { LicenseChangeDirection } from "@KassaroAB/shared/types/enums/LicenseChangeDirection";
import { LicenseChangeStatus } from "@KassaroAB/shared/types/enums/LicenseChangeStatus";
import { Card } from "../../components/Card";
import { LoadingContainer } from "../../components/LoadingContainer";
import { SectionTitle } from "../../components/SectionTitle";
import { SelectInput } from "../../components/SelectInput";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../components/Table";
import { formatDate } from "../../utils/dateUtils";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const ALL = "ALL";

type DirectionFilter = typeof ALL | LicenseChangeDirection;

const TableBox = styled(Card)`
  background-color: ${colors.background};
  margin-bottom: ${spacing.x2}px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  flex-wrap: wrap;
  margin-top: ${spacing.x3}px;
  margin-bottom: ${spacing.x2}px;
`;

const FilterBox = styled.div`
  width: 240px;
`;

const EmptyState = styled.div`
  padding: ${spacing.x4}px;
  text-align: center;
  color: ${colors.textMuted};
`;

const EntityLink = styled(Link)`
  color: ${colors.primary};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const QuantityLabel = styled.span<{ $added: boolean }>`
  font-weight: 600;
  color: ${({ $added }) => ($added ? colors.successText : colors.dangerText)};
`;

const StatusLabel = styled.span<{ $highlight: boolean }>`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${({ $highlight }) => ($highlight ? colors.text : colors.textMuted)};
`;

const TypeTag = styled.span`
  margin-left: ${spacing.x1}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const STATUS_KEYS: Record<LicenseChangeStatus, string> = {
  [LicenseChangeStatus.NEW]: "licenseChanges.statusNew",
  [LicenseChangeStatus.TERMINATED]: "licenseChanges.statusTerminated",
  [LicenseChangeStatus.CHANGED]: "licenseChanges.statusChanged",
  [LicenseChangeStatus.QUANTITY_CHANGED]: "licenseChanges.statusQuantityChanged",
};

const sumQuantity = (
  entries: RestLicenseChangeEntry[],
  direction: LicenseChangeDirection
) =>
  entries
    .filter((entry) => entry.direction === direction)
    .reduce((total, entry) => total + entry.quantity, 0);

const isDirectionFilter = (value: string): value is DirectionFilter =>
  value === ALL ||
  value === LicenseChangeDirection.ADDED ||
  value === LicenseChangeDirection.REMOVED;

interface LicenseChangesTableProps {
  isLoading: boolean;
  entries: RestLicenseChangeEntry[];
  showReseller: boolean;
}

export const LicenseChangesTable = ({
  isLoading,
  entries,
  showReseller,
}: LicenseChangesTableProps) => {
  const { t } = useTranslation();
  const [direction, setDirection] = useState<DirectionFilter>(ALL);

  const addedTotal = sumQuantity(entries, LicenseChangeDirection.ADDED);
  const removedTotal = sumQuantity(entries, LicenseChangeDirection.REMOVED);
  const visibleEntries = useMemo(
    () =>
      direction === ALL
        ? entries
        : entries.filter((entry) => entry.direction === direction),
    [entries, direction]
  );

  const renderContent = () => {
    if (isLoading) {
      return <LoadingContainer message={t("statistics.loading")} />;
    }
    if (visibleEntries.length === 0) {
      return <EmptyState>{t("licenseChanges.empty")}</EmptyState>;
    }
    return (
      <Table minWidth={showReseller ? "760px" : "620px"}>
        <TableHeader>
          <tr>
            <TableHeaderCell>{t("licenseChanges.store")}</TableHeaderCell>
            {showReseller && (
              <TableHeaderCell>{t("licenseChanges.reseller")}</TableHeaderCell>
            )}
            <TableHeaderCell>{t("licenseChanges.license")}</TableHeaderCell>
            <TableHeaderCell>{t("licenseChanges.change")}</TableHeaderCell>
            <TableHeaderCell>{t("licenseChanges.date")}</TableHeaderCell>
            <TableHeaderCell>{t("licenseChanges.status")}</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {visibleEntries.map((entry) => {
            const added = entry.direction === LicenseChangeDirection.ADDED;
            return (
              <TableRow key={`${entry.storeId}-${entry.billingItemId}`}>
                <TableCell>
                  <EntityLink to={`/stores/${entry.storeId}/overview`}>
                    {entry.storeName}
                  </EntityLink>
                </TableCell>
                {showReseller && (
                  <TableCell>
                    <EntityLink to={`/resellers/${entry.resellerId}/overview`}>
                      {entry.resellerName}
                    </EntityLink>
                  </TableCell>
                )}
                <TableCell>
                  {entry.name}
                  {entry.type !== BillingItemType.PACKAGE && (
                    <TypeTag>{t(`billingItems.type.${entry.type}`)}</TypeTag>
                  )}
                </TableCell>
                <TableCell noWrap>
                  <QuantityLabel $added={added}>
                    {added ? `+${entry.quantity}` : `−${entry.quantity}`}
                  </QuantityLabel>
                </TableCell>
                <TableCell noWrap>{formatDate(entry.date)}</TableCell>
                <TableCell>
                  <StatusLabel
                    $highlight={
                      entry.status === LicenseChangeStatus.NEW ||
                      entry.status === LicenseChangeStatus.TERMINATED
                    }
                  >
                    {t(STATUS_KEYS[entry.status])}
                  </StatusLabel>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <>
      <Header>
        <SectionTitle>{t("licenseChanges.sectionTitle")}</SectionTitle>
        <FilterBox>
          <SelectInput
            value={direction}
            onChange={(event) => {
              if (isDirectionFilter(event.target.value)) {
                setDirection(event.target.value);
              }
            }}
          >
            <option value={ALL}>{t("licenseChanges.filterAll")}</option>
            <option value={LicenseChangeDirection.ADDED}>
              {t("licenseChanges.filterAdded", { count: addedTotal })}
            </option>
            <option value={LicenseChangeDirection.REMOVED}>
              {t("licenseChanges.filterRemoved", { count: removedTotal })}
            </option>
          </SelectInput>
        </FilterBox>
      </Header>
      <TableBox>{renderContent()}</TableBox>
    </>
  );
};
